import AudienceManager from '../audienceManager';
import CustomSnackBar from '../audienceManager/customSnackBar';
import GlobalHeader from 'components/GlobalHeader/GlobalHeader';
import Login from 'pages/auth';
import MaintenanceWrapper from 'components/MaintenanceWrapper';
import Navigation from 'components/navigation';
import OAuthUrls from '../oauthUrls';
import OtsSegmentDetail from '../otsSegmentManager/otsSegmentDetail';
import OtsSegmentManager from '../otsSegmentManager';
import React, { useEffect, useState } from 'react';

import Spinner from 'components/spinner';
import isVersionChanged, { setLatestVersion } from 'utils/versionCheck';
import tokens from 'tokens';
import { AudienceSelector } from '../audienceManager/audienceTypeSelector';
import { Box, Grid, Theme, createStyles, makeStyles } from '@material-ui/core';
import { ILoginReducer } from 'types/types';
import { LoadingStates, Routes } from '../../constants/index';
import { RESET_ALL_REDUCERS } from 'redux/actions';
import { RootStateOrAny, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { asyncWithLDProvider, useFlags } from 'launchdarkly-react-client-sdk';
import { theme as kloverTheme } from '@attain-sre/attain-design-system';
import {
  removeNotification as removeAudienceNotification,
  setAudienceLoadingState,
} from '../audienceManager/slice';

import AudienceEditorRoute, {
  AudienceRedirectRoute,
} from 'pages/audienceManager/editorRoute';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useHistory } from 'react-router';
import { useSigninCheck, useUser } from 'reactfire';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      height: '100vh',
    },
    title: {
      color: tokens.colorBodynegative,
      fontWeight: 600,
      lineHeight: '40px',
      paddingBottom: tokens.spacing.lg,
    },
    grid: {
      marginLeft: 250,
      flexGrow: 1,
      marginTop: 64,
    },
    notificationBadge: {
      padding: '8px',
      marginLeft: '-6px',
      color: '#DADEE2 !important',
      '&:hover': {
        color: 'white',
      },
      '&:focus': {
        color: 'white',
      },
    },
    profile: {
      position: 'fixed',
      bottom: 24,
      left: 20,
    },
    main: {
      backgroundColor: tokens.colorPagebg,
    },
    greyBackground: {
      backgroundColor: 'rgb(240, 239, 241)',
    },
    header: {
      gridArea: 'header',
      position: 'fixed',
      top: 0,
      height: '64px',
      width: '100%',
      zIndex: 2,
    },
    headerGrid: {
      display: 'grid',
    },
    nav: {
      gridArea: 'nav',
      height: 'auto',
      position: 'fixed',
      top: 64,
      width: 250,
      zIndex: 'auto',
      background: kloverTheme.colors.backgroundBrand,
      color: kloverTheme.colors.textInverted,
    },
  })
);

// Inner component that uses useFlags() - must be inside LDProvider context
const RoutesSwitch: React.FC = () => {
  const { uiOtsSegmentManagerVisible = false } = useFlags();

  return (
    <Switch>
      <Route exact path={Routes.AUDIENCE_MANAGER}>
        <AudienceRedirectRoute />
      </Route>
      <Route exact path={Routes.CUSTOM_AUDIENCE_MANAGER}>
        <AudienceRedirectRoute />
      </Route>
      <Route exact path={Routes.AUDIENCE_STUDIO}>
        <AudienceManager />
      </Route>
      <Route path={`${Routes.AUDIENCE_MANAGER}/editor/:audienceid?`}>
        <AudienceRedirectRoute />
      </Route>
      <Route path={`${Routes.CUSTOM_AUDIENCE_MANAGER}/editor/:audienceid?`}>
        <AudienceRedirectRoute />
      </Route>
      <Route path={`${Routes.AUDIENCE_STUDIO_EDITOR}/:audienceid?`}>
        <AudienceEditorRoute />
      </Route>
      <Route path={Routes.AUDIENCE_STUDIO_AUDIENCE_SELECTOR}>
        <AudienceSelector />
      </Route>
      <Route exact path={Routes.OAUTH_URLS}>
        <OAuthUrls />
      </Route>
      {uiOtsSegmentManagerVisible && (
        <>
          <Route exact path={Routes.OTS_SEGMENT_MANAGER}>
            <OtsSegmentManager />
          </Route>
          <Route path={Routes.OTS_SEGMENT_MANAGER_DETAIL}>
            <OtsSegmentDetail />
          </Route>
        </>
      )}
    </Switch>
  );
};

const Main: React.FC = () => {
  const dispatch = useAppDispatch();
  const { status, data: signInCheckResult } = useSigninCheck();
  const history = useHistory();
  const currentPath = history.location.pathname;
  const classes = useStyles();
  const { data: user } = useUser();
  const [LDProvider, setLDProvider] = useState<React.FC | null>(null);
  const [isLDLoading, setIsLDLoading] = useState(true);
  const isVersionUpdated = isVersionChanged();

  // Reducers
  const authData: ILoginReducer = useSelector(
    (state: RootStateOrAny) => state.loginReducer
  );

  const audienceManagerNotifications = useAppSelector(
    (state) => state.audienceReducer.ui.notifications
  );

  useEffect(() => {
    if (isVersionUpdated) {
      setLatestVersion();
      dispatch({ type: RESET_ALL_REDUCERS });
    }
    if (!authData.isLoggedIn) {
      history.push('/');
    }
  }, [authData, history]);

  useEffect(() => {
    (async () => {
      if (user) {
        try {
          const LDProviderComponent = await asyncWithLDProvider({
            clientSideID: process.env.LAUNCHDARKLY_PROJECT_CLIENT_SIDE_ID,
            user: {
              key: user?.uid,
              name: user?.email,
              email: user?.email,
            },
          });
          setLDProvider(() => LDProviderComponent);
          setIsLDLoading(false);
        } catch (error) {
          console.error('LaunchDarkly initialization failed:', error);
          setIsLDLoading(false);
        }
      }
    })();
    dispatch(setAudienceLoadingState({ state: LoadingStates.DONE }));
  }, [user]);

  React.useEffect(() => {
    dispatch(setAudienceLoadingState({ state: LoadingStates.DONE }));
  }, []);

  if (status === 'loading' || isLDLoading) {
    return <Spinner />;
  }

  if (signInCheckResult.signedIn === true) {
    const mainComponent = (
      <MaintenanceWrapper>
        <div className={classes.root}>
          <header className={classes.header}>
            <GlobalHeader />
          </header>
          <nav className={classes.nav}>
            <Navigation />
          </nav>
          <Grid container spacing={0}>
            <Grid item className={classes.grid}>
              <div
                style={
                  currentPath.includes('audience-studio/editor')
                    ? { height: '92vh', background: 'rgb(240, 239, 241)' }
                    : {}
                }
              >
                <Box
                  className={
                    currentPath.includes('audience-studio/editor')
                      ? classes.greyBackground
                      : classes.main
                  }
                  mt={0}
                  ml={0}
                >
                  <RoutesSwitch />
                </Box>
              </div>
            </Grid>
          </Grid>
          {audienceManagerNotifications.map(({ state, message }, i) => (
            <CustomSnackBar
              index={i}
              trigger={true}
              severity={
                state === 'done'
                  ? 'success'
                  : state === 'warning'
                  ? 'warning'
                  : 'error'
              }
              message={message || ''}
              key={i}
              removeNotificationFunction={removeAudienceNotification}
            />
          ))}
        </div>
      </MaintenanceWrapper>
    );

    if (LDProvider) {
      return <LDProvider>{mainComponent}</LDProvider>;
    }

    return mainComponent;
  } else {
    return <Login />;
  }
};

export default Main;
